/**
 * The tasks import adapter — `KLQ-20260824-002`'s first v1 surface.
 *
 * A pasted row is a task: a title, the place it hangs, and whatever else the sheet carries.
 * The studio's existing task list becomes their register, and the register pasted back into
 * itself becomes nothing at all — which is the property this file exists to guarantee.
 *
 * FIVE DECISIONS, ALL OF THEM CONSTRAINED BY WHAT THE WRITE PATH ALREADY IS
 *
 * 1. **There is no second write path.** Rows are created through
 *    `POST /projects/{id}/task-generations` with explicit per-node `titles` — the one field of
 *    variance `KLQ-20260825-001` §8 added for exactly this caller. Its dry run *is* the
 *    preview, so what a person approved and what gets written are computed by the same code on
 *    the same server. Building a bulk-create endpoint beside it would be retro rule 3's two
 *    owners of one mechanism.
 *
 * 2. **The duplicate rule is (location, title), and it is the server's.**
 *    `_already_generated` skips a row whose exact title already exists on that node, which is
 *    what makes a re-paste of a register a no-op rather than a second copy of everything. It is
 *    deliberately not "the task number the sheet carries": a number is ours, not the sheet's,
 *    and a studio's own list has never seen one. Two genuinely different tasks with the same
 *    title on one floor are the case this loses, and the sheet cannot tell them apart either.
 *
 * 3. **`titles` is keyed by node, so one request writes at most one task per location.** A
 *    sheet with three tasks on floor 3 is therefore split into three *rounds*, each a request.
 *    That is the shape of the endpoint rather than a choice made here, and rounds are cheap
 *    because a normal sheet has one row per place.
 *
 * 4. **`kind` is per request; the rest is per row and lands afterwards.** The generation body
 *    carries one kind, so rows are bucketed by kind (three at most). Priority, due date and
 *    assignees are not on that body at all — assignees deliberately so
 *    (`KLQ-20260825-001` scope) — and are applied to the rows just written with the same
 *    `bulk-edit` the grid's own fan-out uses, grouped by identical value. No new endpoint, and
 *    every value passes the same validation the drawer's would.
 *
 * 5. **A location may be named by a header row.** Copying the grouped hierarchy puts the floor
 *    on its own line with every other cell empty, and that is also how a person writes a list
 *    by hand. So a row whose only filled cell is the title, *and whose text resolves to a node
 *    in this project*, sets the location for the rows beneath it. The second half of that
 *    condition is what keeps an ordinary title-only task row from being swallowed as a
 *    heading: if it names no place, it is work.
 *
 * **Nothing here decides visibility.** The hierarchy arrives already pruned to the caller's
 * zones, so a location the sheet names and this person cannot see simply matches nothing and
 * fails its row — which is the same answer the server would give, arrived at without a probe.
 */

import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { hierarchyQueryOptions } from "@/features/drawings";
import { membersQueryOptions } from "@/features/people";
import type {
  ImportAdapter,
  ImportField,
  ImportMapping,
  ImportOutcome,
  ImportPreview,
  ImportRow,
} from "@/lib/grid-interop";
import { cellOf, normalizeHeader } from "@/lib/grid-interop";
import { useMessages } from "@/lib/i18n";
import type { Translate } from "@/lib/i18n";

import { bulkEditTasks, generateTasks } from "./api";
import type { TaskGenerate, TaskKind, TaskPriority } from "./api";
import { detailOf } from "./problems";

/** The fields a sheet may feed. `title` and `location` are what a task cannot exist without. */
function fieldsFor(t: Translate<"tasks">): readonly ImportField[] {
  return [
    {
      key: "title",
      label: t("import.field.title"),
      required: true,
      // The first entry of each list is what *this* product's own copy produces, so a sheet
      // made by copying the table round-trips. The rest is what a studio's own sheet says.
      synonyms: [
        "work",
        "what",
        "title",
        "task",
        "name",
        "praca",
        "co",
        "nazov",
        "uloha",
        "arbeit",
        "was",
        "titel",
        "aufgabe",
      ],
    },
    {
      key: "location",
      label: t("import.field.location"),
      synonyms: [
        "where",
        "location",
        "place",
        "node",
        "level",
        "kde",
        "miesto",
        "lokalita",
        "umiestnenie",
        "uroven",
        "wo",
        "ort",
        "bereich",
        "ebene",
      ],
    },
    {
      key: "kind",
      label: t("import.field.kind"),
      synonyms: ["kind", "type", "druh", "typ", "art"],
    },
    {
      key: "priority",
      label: t("import.field.priority"),
      synonyms: ["priority", "priorita", "prioritat"],
    },
    {
      key: "due",
      label: t("import.field.due"),
      synonyms: [
        "when",
        "due",
        "due date",
        "deadline",
        "kedy",
        "termin",
        "datum",
        "wann",
        "fallig",
        "frist",
      ],
    },
    {
      key: "assignees",
      label: t("import.field.assignees"),
      synonyms: [
        "who",
        "assignee",
        "assignees",
        "owner",
        "kto",
        "riesitel",
        "zodpovedny",
        "wer",
        "bearbeiter",
        "zustandig",
      ],
    },
  ];
}

/** One pasted line, understood. `place` is `null` until a location is resolved for it. */
interface ParsedRow {
  readonly index: number;
  /** A heading naming a location, rather than work. Never written. */
  readonly heading: boolean;
  readonly nodeId: string | null;
  readonly title: string;
  readonly kind: TaskKind;
  readonly priority: TaskPriority | null;
  readonly dueAt: string | null;
  readonly assignees: readonly string[];
  /** The first thing wrong with this row, already translated. */
  readonly error: string | null;
  /** Where it would land, for the preview's own column. */
  readonly where: string;
}

/**
 * Every way a person might write a place, pointing at the node it means.
 *
 * A node is reachable by its code, its name, its code path and its name path — an architect
 * writes `1.02`, `Byt 1.02`, `A/1/1.02` and `Blok A / 1.NP / Byt 1.02` and means one thing by
 * all four. A key that two nodes answer to is dropped rather than resolved by position: with
 * `1.02` on eight blocks, picking the first would file a whole floor's work in the wrong
 * building, and the person can disambiguate with a path in one edit.
 */
function locationIndex(
  nodes: readonly { id: string; name: string; code: string; parent_id?: string | null }[],
): ReadonlyMap<string, string | null> {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const keysOf = (node: (typeof nodes)[number]): string[] => {
    const codes: string[] = [];
    const names: string[] = [];
    let cursor: (typeof nodes)[number] | undefined = node;
    // Bounded by the tree's depth; the guard is against a cycle a bad fixture could produce,
    // not against a real project.
    for (let hops = 0; cursor !== undefined && hops < 32; hops += 1) {
      codes.unshift(cursor.code);
      names.unshift(cursor.name);
      cursor =
        cursor.parent_id == null ? undefined : byId.get(cursor.parent_id);
    }
    return [
      node.code,
      node.name,
      codes.join("."),
      codes.join("/"),
      names.join("/"),
      `${node.code} ${node.name}`,
    ];
  };

  const index = new Map<string, string | null>();
  for (const node of nodes) {
    for (const key of keysOf(node)) {
      const normalized = normalizeHeader(key);
      if (normalized === "") continue;
      // `null` marks a key more than one node answers to — see the note above.
      index.set(
        normalized,
        index.has(normalized) && index.get(normalized) !== node.id
          ? null
          : node.id,
      );
    }
  }
  return index;
}

/** A word, as one of a fixed set. `undefined` when the sheet said something else. */
function pick<T extends string>(
  input: string,
  table: Readonly<Record<T, readonly string[]>>,
): T | undefined {
  const wanted = normalizeHeader(input);
  for (const [value, words] of Object.entries(table) as [
    T,
    readonly string[],
  ][]) {
    if (words.some((word) => normalizeHeader(word) === wanted)) return value;
  }
  return undefined;
}

/**
 * A calendar day out of a sheet, as the instant it starts in UTC.
 *
 * Two shapes and no more: ISO (`2026-09-30`), which is what this product copies out and what
 * Sheets writes in every locale once a cell is a date, and dotted European (`30.9.2026`),
 * which is what a Slovak studio types. Anything else fails the row rather than being guessed
 * at — `09/10/2026` is two different days on two sides of an ocean, and a silently wrong
 * deadline is worse than a refused one.
 */
function parseDay(input: string): string | null | undefined {
  if (input === "") return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  const dotted = /^(\d{1,2})\.\s?(\d{1,2})\.\s?(\d{4})$/.exec(input);
  const parts = iso
    ? [iso[1], iso[2], iso[3]]
    : dotted
      ? [dotted[3], dotted[2], dotted[1]]
      : null;
  if (parts === null) return undefined;
  const [year, month, day] = parts.map((part) => Number(part));
  if (year === undefined || month === undefined || day === undefined)
    return undefined;
  const at = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(at.getTime()) ||
    at.getUTCMonth() !== month - 1 ||
    at.getUTCDate() !== day
  )
    return undefined;
  return at.toISOString();
}

/** The generation request one bucket of rows makes. One kind, one title per node. */
interface Bucket {
  readonly kind: TaskKind;
  readonly rows: readonly ParsedRow[];
}

/**
 * Rows, as requests: bucketed by kind, then split into rounds so that no request names a node
 * twice. See decision 3 — the split is the endpoint's shape, not a policy.
 */
function bucketize(rows: readonly ParsedRow[]): readonly Bucket[] {
  const buckets: Bucket[] = [];
  for (const kind of ["task", "issue", "question"] as const) {
    const mine = rows.filter((row) => row.kind === kind);
    let round = mine;
    while (round.length > 0) {
      const seen = new Set<string>();
      const here: ParsedRow[] = [];
      const later: ParsedRow[] = [];
      for (const row of round) {
        const at = row.nodeId ?? "";
        if (seen.has(at)) later.push(row);
        else {
          seen.add(at);
          here.push(row);
        }
      }
      if (here.length > 0) buckets.push({ kind, rows: here });
      round = later;
    }
  }
  return buckets;
}

function bodyFor(bucket: Bucket, dryRun: boolean): TaskGenerate {
  const titles: Record<string, string> = {};
  for (const row of bucket.rows) {
    if (row.nodeId !== null) titles[row.nodeId] = row.title;
  }
  return {
    // `under: []` is *the whole project within this caller's scope*; `titles` then narrows it
    // to the nodes named. Sending the ids as `under` instead would make an unreachable one a
    // `404` for the whole request, which is exactly the per-row failure this preview avoids.
    under: [],
    titles,
    kind: bucket.kind,
    claim_deliverables: false,
    dry_run: dryRun,
  };
}

export interface TaskImportAdapter extends ImportAdapter {
  /** Nothing can be understood before the tree and the member list have arrived. */
  readonly ready: boolean;
}

export function useTaskImportAdapter(projectId: string): TaskImportAdapter {
  const t = useMessages("tasks");
  const hierarchy = useQuery(hierarchyQueryOptions(projectId));
  const members = useQuery(membersQueryOptions(projectId));

  const fields = useMemo(() => fieldsFor(t), [t]);
  const places = useMemo(
    () => locationIndex(hierarchy.data?.nodes ?? []),
    [hierarchy.data],
  );

  /** A person, by whatever the sheet calls them: display name or email, accent-blind. */
  const people = useMemo(() => {
    const index = new Map<string, string | null>();
    for (const member of members.data?.items ?? []) {
      for (const key of [member.display_name, member.email]) {
        if (key == null || key === "") continue;
        const normalized = normalizeHeader(key);
        index.set(
          normalized,
          index.has(normalized) && index.get(normalized) !== member.user_id
            ? null
            : member.user_id,
        );
      }
    }
    return index;
  }, [members.data]);

  const KIND_WORDS: Readonly<Record<TaskKind, readonly string[]>> = useMemo(
    () => ({
      task: ["task", t("kind.task"), "uloha", "aufgabe"],
      issue: ["issue", t("kind.issue"), "problem", "chyba"],
      question: ["question", t("kind.question"), "otazka", "frage"],
    }),
    [t],
  );

  const PRIORITY_WORDS: Readonly<Record<TaskPriority, readonly string[]>> =
    useMemo(
      () => ({
        low: ["low", t("priority.low"), "nizka", "niedrig"],
        normal: ["normal", t("priority.normal"), "bezna", "stredna"],
        high: ["high", t("priority.high"), "vysoka", "hoch"],
      }),
      [t],
    );

  /**
   * The whole sheet, understood, in one pass — because the location of a row can come from a
   * heading above it, and a pass that looked at rows independently could not see that.
   */
  const parseRows = useCallback(
    (
      grid: readonly (readonly string[])[],
      mapping: ImportMapping,
    ): readonly ParsedRow[] => {
      const nameOf = new Map<string, string>();
      for (const node of hierarchy.data?.nodes ?? []) nameOf.set(node.id, node.name);

      let heading: { readonly id: string; readonly label: string } | null = null;
      return grid.map((line, index): ParsedRow => {
        const title = cellOf(line, mapping, "title");
        const location = cellOf(line, mapping, "location");
        const kindText = cellOf(line, mapping, "kind");
        const priorityText = cellOf(line, mapping, "priority");
        const dueText = cellOf(line, mapping, "due");
        const assigneeText = cellOf(line, mapping, "assignees");

        const bare =
          location === "" &&
          kindText === "" &&
          priorityText === "" &&
          dueText === "" &&
          assigneeText === "";

        // Decision 5. A title-only row that names a place *is* a place; one that does not is
        // work with nothing else said about it, which is a perfectly ordinary sheet row.
        if (bare && title !== "") {
          const found = places.get(normalizeHeader(title));
          if (found != null) {
            heading = { id: found, label: title };
            return {
              index,
              heading: true,
              nodeId: found,
              title,
              kind: "task",
              priority: null,
              dueAt: null,
              assignees: [],
              error: null,
              where: title,
            };
          }
        }

        let error: string | null = null;
        // The **first** reason wins: a row that has no location and no slot is reported as
        // missing its location, not as missing both. `??=` is that sentence — it writes only
        // while nothing has been written.
        const fail = (reason: string): void => {
          error ??= reason;
        };

        let nodeId: string | null = null;
        let where = "";
        if (location !== "") {
          const found = places.get(normalizeHeader(location));
          if (found === undefined) fail(t("import.reason.unknownLocation"));
          else if (found === null) fail(t("import.reason.ambiguousLocation"));
          else {
            nodeId = found;
            where = location;
          }
        } else if (heading !== null) {
          nodeId = heading.id;
          where = heading.label;
        } else {
          fail(t("import.reason.noLocation"));
        }

        if (title === "") fail(t("import.reason.noTitle"));

        const kind =
          kindText === "" ? "task" : (pick(kindText, KIND_WORDS) ?? "task");
        if (kindText !== "" && pick(kindText, KIND_WORDS) === undefined)
          fail(t("import.reason.badKind", { value: kindText }));

        let priority: TaskPriority | null = null;
        if (priorityText !== "") {
          const chosen = pick(priorityText, PRIORITY_WORDS);
          if (chosen === undefined)
            fail(t("import.reason.badPriority", { value: priorityText }));
          else priority = chosen;
        }

        const day = parseDay(dueText);
        if (day === undefined) fail(t("import.reason.badDate", { value: dueText }));

        const assignees: string[] = [];
        for (const piece of assigneeText.split(/[,;]/)) {
          const name = piece.trim();
          if (name === "") continue;
          const found = people.get(normalizeHeader(name));
          if (found == null) fail(t("import.reason.unknownAssignee", { name }));
          else assignees.push(found);
        }

        return {
          index,
          heading: false,
          nodeId,
          title,
          kind,
          priority,
          dueAt: day ?? null,
          assignees,
          error,
          where: where === "" ? (nodeId === null ? "" : (nameOf.get(nodeId) ?? "")) : where,
        };
      });
    },
    [hierarchy.data, places, people, t, KIND_WORDS, PRIORITY_WORDS],
  );

  const preview = useCallback(
    async (
      grid: readonly (readonly string[])[],
      mapping: ImportMapping,
    ): Promise<ImportPreview> => {
      const parsed = parseRows(grid, mapping);
      const verdicts = new Map<number, ImportRow>();
      for (const row of parsed) {
        if (row.heading)
          verdicts.set(row.index, {
            index: row.index,
            verdict: "skip",
            summary: row.title,
            where: row.where,
            reason: t("import.reason.locationRow"),
          });
        else if (row.error !== null)
          verdicts.set(row.index, {
            index: row.index,
            verdict: "error",
            summary: row.title,
            where: row.where,
            reason: row.error,
          });
      }

      const candidates = parsed.filter(
        (row) => !row.heading && row.error === null && row.nodeId !== null,
      );
      // The dry run is the preview: one round trip per bucket, writing nothing, and the same
      // computation the write will perform. Run together rather than in sequence — they are
      // independent, and a 200-row sheet with three kinds should not cost three waits.
      await Promise.all(
        bucketize(candidates).map(async (bucket) => {
          try {
            const plan = await generateTasks(projectId, bodyFor(bucket, true));
            const already = new Set(
              plan.skipped.map(
                (row) => `${row.hierarchy_node_id}${row.title}`,
              ),
            );
            for (const row of bucket.rows) {
              const duplicate = already.has(`${row.nodeId ?? ""}${row.title}`);
              verdicts.set(row.index, {
                index: row.index,
                verdict: duplicate ? "skip" : "create",
                summary: row.title,
                where: row.where,
                reason: duplicate ? t("import.reason.exists") : null,
              });
            }
          } catch (error: unknown) {
            // A refusal is this bucket's answer, not the sheet's: the rows it names carry the
            // server's own sentence and the other buckets still report normally.
            const reason = detailOf(error) ?? t("import.reason.refused");
            for (const row of bucket.rows) {
              verdicts.set(row.index, {
                index: row.index,
                verdict: "error",
                summary: row.title,
                where: row.where,
                reason,
              });
            }
          }
        }),
      );

      const rows = parsed.map(
        (row) =>
          verdicts.get(row.index) ?? {
            index: row.index,
            verdict: "error" as const,
            summary: row.title,
            where: row.where,
            reason: t("import.reason.refused"),
          },
      );
      return { rows, note: null };
    },
    [projectId, parseRows, t],
  );

  const commit = useCallback(
    async (
      grid: readonly (readonly string[])[],
      mapping: ImportMapping,
      keep: readonly number[],
    ): Promise<ImportOutcome> => {
      const wanted = new Set(keep);
      const rows = parseRows(grid, mapping).filter(
        (row) =>
          !row.heading &&
          row.error === null &&
          row.nodeId !== null &&
          wanted.has(row.index),
      );

      const failed: { index: number; reason: string }[] = [];
      /** What was actually written, so the second pass can name the ids it must edit. */
      const written = new Map<number, string>();

      // Sequential and not parallel: the rounds of one kind name the same nodes, and the
      // duplicate check each of them runs has to see what the previous one wrote.
      for (const bucket of bucketize(rows)) {
        try {
          const plan = await generateTasks(projectId, bodyFor(bucket, false));
          const idOf = new Map(
            plan.planned
              .filter((row) => row.id != null)
              .map((row) => [
                `${row.hierarchy_node_id}${row.title}`,
                row.id ?? "",
              ]),
          );
          for (const row of bucket.rows) {
            const id = idOf.get(`${row.nodeId ?? ""}${row.title}`);
            if (id === undefined || id === "")
              failed.push({
                index: row.index,
                reason: t("import.reason.exists"),
              });
            else written.set(row.index, id);
          }
        } catch (error: unknown) {
          const reason = detailOf(error) ?? t("import.reason.refused");
          for (const row of bucket.rows) failed.push({ index: row.index, reason });
        }
      }

      /*
       * Decision 4's second half. Priority, due date and assignees are not on the generation
       * body, so they land on the rows just written through the same `bulk-edit` the grid's
       * own fan-out uses — grouped by identical value, which is what keeps a 200-row sheet to
       * a handful of requests rather than 200.
       */
      const groups = new Map<string, { rows: ParsedRow[]; taskIds: string[] }>();
      for (const row of rows) {
        const id = written.get(row.index);
        if (id === undefined) continue;
        if (
          row.priority === null &&
          row.dueAt === null &&
          row.assignees.length === 0
        )
          continue;
        const key = JSON.stringify([row.priority, row.dueAt, row.assignees]);
        const bucket = groups.get(key) ?? { rows: [], taskIds: [] };
        bucket.rows.push(row);
        bucket.taskIds.push(id);
        groups.set(key, bucket);
      }

      for (const group of groups.values()) {
        const sample = group.rows[0];
        if (sample === undefined) continue;
        try {
          await bulkEditTasks(projectId, {
            task_ids: group.taskIds,
            clear_due_at: false,
            ...(sample.dueAt === null ? {} : { due_at: sample.dueAt }),
            ...(sample.priority === null ? {} : { priority: sample.priority }),
            ...(sample.assignees.length === 0
              ? {}
              : { assignees: { add: [...sample.assignees] } }),
          });
        } catch (error: unknown) {
          // The task exists — only the extra fields did not land. Reported per row, and never
          // as a failure to create, because the row *is* in the register now.
          const reason = detailOf(error) ?? t("import.reason.refused");
          for (const row of group.rows)
            failed.push({ index: row.index, reason });
        }
      }

      return { written: written.size, failed, note: null };
    },
    [projectId, parseRows, t],
  );

  return useMemo(
    () => ({
      fields,
      preview,
      commit,
      ready: hierarchy.isSuccess && members.isSuccess,
    }),
    [fields, preview, commit, hierarchy.isSuccess, members.isSuccess],
  );
}
